import React, { useState, useCallback, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import useAuth from '../../../../features/hooks/useAuth';
import { useSelector } from 'react-redux';
import { CommonButton } from '../../../../Styles/CommonStyles';
import { useGetUsersQuery } from '../../../../features/users/usersApiSlice';
import { useGetofficesQuery } from '../../../../features/offices/officesApiSlice';
import { useGetStoreOfficesQuery } from '../../../../features/storeoffices/storeofficesApiSlice';
import { useUpdateConsumableReqMutation } from '../../../../features/consumablerequest/consumableReqApiSlice';
import nameStringFn from '../../../../data/nameStringFn';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';

// Styled components defined outside the component for better performance
const StyledButton = styled(CommonButton)`
  padding: 8px 16px;
  min-width: 120px;
  margin-right: 10px;
`;

const ForwardButton = styled(StyledButton)`
  background-color: #4caf50;
`;

const ReturnButton = styled(StyledButton)`
  background-color: #ff9800;
`;

const RemarkContainer = styled.div`
  margin-top: 15px;
  width: 100%;
`;

const StyledTableContainer = styled(TableContainer)`
  margin-bottom: 20px;
  margin-top: 20px;
`;

const StyledTableHead = styled(TableHead)`
  background-color: #1976d2;
  
  & th {
    color: white;
    font-weight: bold;
  }
`;

const SelectedAssetsContainer = styled.div`
  margin-top: 20px;
  width: 100%;
`;

const SelectedAssetsTitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
`;

const SelectedAssetsTitle = styled.h3`
  margin-bottom: 0;
  color: #1976d2;
`;

const SummaryContainer = styled.div`
  margin-bottom: 15px;
  padding: 10px;
  background-color: #f5f5f5;
  border-radius: 5px;
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  background-color: white;
  padding: 5px 10px;
  border-radius: 5px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
`;

const SummaryItemName = styled.span`
  font-weight: 500;
  margin-right: 10px;
`;

const SummaryItemCount = styled.span`
  background-color: #1976d2;
  color: white;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 0.8rem;
`;

const NotificationMessage = styled.div`
  margin-top: 10px;
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 500;
  display: flex;
  align-items: center;
  background-color: ${props => props.isPartial ? '#ff9800' : '#4caf50'};
  color: white;
  margin-bottom: 20px;
`;

// Error handling components
const ErrorMessage = styled.div`
  background-color: #f44336;
  color: white;
  padding: 10px 15px;
  border-radius: 4px;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ErrorCloseButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  font-size: 16px;
  cursor: pointer;
  padding: 0 5px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const LoadingText = styled.div`
  background-color: white;
  padding: 20px;
  border-radius: 8px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 10px;
`;

// Memoized summary component
const ConsumablesSummary = React.memo(({ items, generateSummary }) => (
  <SummaryContainer>
    <span style={{ fontWeight: 'bold', marginRight: '10px' }}>
      Total: {items.reduce((sum, item) => sum + parseInt(item.selectedQuantity || item.quantity), 0)} items
    </span>
    {generateSummary(items).map((item, index) => (
      <SummaryItem key={index}>
        <SummaryItemName>{item.name}</SummaryItemName>
        <SummaryItemCount>{item.count}</SummaryItemCount>
      </SummaryItem>
    ))}
  </SummaryContainer>
));

// Memoized table component
const ConsumablesTable = React.memo(({ items, getRequestedQuantity }) => (
  <Table>
    <StyledTableHead>
      <TableRow>
        <TableCell>Sl.No.</TableCell>
        <TableCell>Consumable</TableCell>
        <TableCell>Asset</TableCell>
        <TableCell>Asset Tag</TableCell>
        <TableCell>Part No</TableCell>
        <TableCell>Requested Qty</TableCell>
        <TableCell>Issued Qty</TableCell>
      </TableRow>
    </StyledTableHead>
    <TableBody>
      {items.map((item, index) => (
        <TableRow key={index}>
          <TableCell>{index + 1}</TableCell>
          <TableCell>{item.consumable || '-'}</TableCell>
          <TableCell>{item.asset || 'Not Applicable'}</TableCell>
          <TableCell>{item.assettag?.label || 'Not Applicable'}</TableCell>
          <TableCell>{item.partno || '-'}</TableCell>
          <TableCell>{getRequestedQuantity(item) || '-'}</TableCell>
          <TableCell>{item.selectedQuantity || item.quantity || '-'}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
));

const StoreL2InboxFormBTS = ({ row, handleBack }) => {
  const [remark, setRemark] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [latestConsumables, setLatestConsumables] = useState([]);
  const [isPartialIssue, setIsPartialIssue] = useState(false);
  
  // Auth and office data
  const auth = useAuth();
  const user_current_office = useSelector((state) => state.office.off);
  
  // API queries with better error handling
  const { getSingleuser, isError: isUsersError, error: usersError } = useGetUsersQuery('userslist', {
    selectFromResult: ({ data, isError, error }) => ({
      getSingleuser: data?.entities,
      isError,
      error
    }),
  });

  const { officedata, isError: isOfficesError, error: officesError } = useGetofficesQuery('offices', {
    selectFromResult: ({ data, isError, error }) => ({
      officedata: data?.entities,
      isError,
      error
    }),
  });

  const { storeoffices, isError: isStoreOfficesError, error: storeOfficesError } = useGetStoreOfficesQuery('storeoffices', {
    selectFromResult: ({ data, isError, error }) => ({
      storeoffices: data?.entities,
      isError,
      error
    }),
  });
  
  // Mutations with better error handling
  const [updateConsumableReq, { isLoading: isUpdatingReq, isError: isUpdateError, error: updateError }] = useUpdateConsumableReqMutation();

  // Set general error state when any API error occurs
  useEffect(() => {
    if (isUsersError) {
      setError(`Error fetching users: ${usersError?.data?.message || 'Unknown error'}`);
    } else if (isOfficesError) {
      setError(`Error fetching offices: ${officesError?.data?.message || 'Unknown error'}`);
    } else if (isStoreOfficesError) {
      setError(`Error fetching store offices: ${storeOfficesError?.data?.message || 'Unknown error'}`);
    } else if (isUpdateError) {
      setError(`Error updating request: ${updateError?.data?.message || 'Unknown error'}`);
    } else {
      setError('');
    }
  }, [
    isUsersError, usersError,
    isOfficesError, officesError,
    isStoreOfficesError, storeOfficesError,
    isUpdateError, updateError
  ]);

  // Get IDs for workflow - memoized
  
  const storeL1_id = useMemo(() => 
    storeoffices ? Object.values(storeoffices).find(item => item?.storeoffice === row?.req_store)?.roleUsers?.L1ReqApprover_C : "",
    [storeoffices, row?.req_store]
  );

  // Generate summary data from consumables - memoized
  const generateSummary = useCallback((items) => {
    if (!items || items.length === 0) return [];

    const summary = items.reduce((acc, item) => {
      const { consumable } = item;
      if (!acc[consumable]) {
        acc[consumable] = 0;
      }
      acc[consumable] += parseInt(item.quantity || item.selectedQuantity);
      return acc;
    }, {});

    return Object.entries(summary).map(([name, count]) => ({ name, count }));
  }, []);

  // Get the latest store consumable issues - memoized
  const getLatestStoreConsumIssue = useCallback(() => {
    if (row?.store_consum_issue && row.store_consum_issue.length > 0) {
      const latestIssue = row.store_consum_issue[row.store_consum_issue.length - 1];
      return latestIssue.data || [];
    }
    return [];
  }, [row?.store_consum_issue]);
  
  // Get the requested quantity for an item from the last consumables_requested object - memoized
  const getRequestedQuantity = useCallback((item) => {
    if (!row?.consumables_requested || row.consumables_requested.length === 0) {
      return item.quantity;
    }
    
    const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
    if (!latestRequest?.data) {
      return item.quantity;
    }
    
    // Compare by consumable, asset, and assettag (not partno, as partno is assigned during issue creation)
    const requestedItem = latestRequest.data.find(reqItem => 
      reqItem.consumable === item.consumable && 
      reqItem.asset === item.asset &&
      (reqItem.assettag?.label === item.assettag?.label)
    );
    
    return requestedItem ? requestedItem.quantity : item.quantity;
  }, [row?.consumables_requested]);

  // Calculate if this is a partial issue by comparing original request with issued items - memoized
  const calculateIsPartialIssue = useCallback(() => {
    if (!row?.consumables_requested || row.consumables_requested.length === 0 || !latestConsumables || latestConsumables.length === 0) {
      return row?.store_partial_issue || false;
    }
    
    const latestRequest = row.consumables_requested[row.consumables_requested.length - 1];
    if (!latestRequest?.data) {
      return row?.store_partial_issue || false;
    }
    
    const requestedItems = latestRequest.data;
    const issuedItems = latestConsumables;
    
    // Check if partial issue based on group totals (to handle splits correctly)
    const issuedGroupTotals = {};
    const requestedGroupTotals = {};
    
    // Calculate totals for issued items by group
    issuedItems.forEach(item => {
      const groupId = item.groupId || 'unknown';
      if (!issuedGroupTotals[groupId]) {
        issuedGroupTotals[groupId] = 0;
      }
      issuedGroupTotals[groupId] += parseInt(item.selectedQuantity || item.quantity, 10);
    });
    
    // Calculate totals for requested items by group
    requestedItems.forEach((item, index) => {
      const groupId = `group-${index}`;
      requestedGroupTotals[groupId] = parseInt(item.quantity, 10);
    });
    
    // Check if any group has less total quantity than requested
    const isPartial = Object.keys(requestedGroupTotals).some(groupId => {
      const issuedTotal = issuedGroupTotals[groupId] || 0;
      const requestedTotal = requestedGroupTotals[groupId];
      return issuedTotal < requestedTotal;
    });
    
    return isPartial;
  }, [row?.consumables_requested, latestConsumables, row?.store_partial_issue]);

  // Initialize latest consumables and calculate partial status
  useEffect(() => {
    const fetchedConsumables = getLatestStoreConsumIssue();
    setLatestConsumables(fetchedConsumables);
  }, [getLatestStoreConsumIssue]);

  // Calculate partial issue status whenever latest consumables change
  useEffect(() => {
    if (latestConsumables.length > 0) {
      const isPartial = calculateIsPartialIssue();
      setIsPartialIssue(isPartial);
    }
  }, [latestConsumables, calculateIsPartialIssue]);

  // Handle returning the request to Store Level 1 for modification - memoized
  const handleReturnToStoreL1 = useCallback(async () => {
    if (!remark.trim()) {
      alert("Please provide a remark explaining what needs to be modified.");
      return;
    }
    
    try {
      setIsLoading(true);
      
      const senttoData = {
        sent_to_tag: "storel2_bts_return_l1",
        Text: remark,
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: storeL1_id, 
          name: getSingleuser?.[storeL1_id]?.name, 
          office: user_current_office, 
          designation: getSingleuser?.[storeL1_id]?.designation?.[user_current_office] 
        },
        action: "Request returned to Store Level 1 for modification",
        curr_sent_to_string: `${nameStringFn(getSingleuser?.[storeL1_id])}-${user_current_office}-${getSingleuser?.[storeL1_id]?.designation?.[user_current_office] || 'Unknown'}`,
        time_stamp: new Date()
      };
      
      const body = { id: row?._id, senttoData: senttoData };
      
      // No need to revert inventory - items stay in transit
      // L1 will handle any adjustments based on modifications
      
      await updateConsumableReq(body).unwrap();
      alert("Request returned to Store Level 1 for modification.");
      handleBack();
    } catch (err) {
      setError(`Failed to return request: ${err.message || 'Unknown error'}`);
      console.error("Error returning request:", err);
      alert("There was an error returning the request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [
    remark, auth, getSingleuser, user_current_office, 
    storeL1_id, row?._id, updateConsumableReq, handleBack, setError
  ]);

  // Handle forwarding the request to BO for acceptance - memoized
  const handleForwardToBO = useCallback(async () => {
    if (!remark.trim()) {
      alert("Please provide a remark before forwarding.");
      return;
    }
    
    try {
      setIsLoading(true);
      
      const senttoData = {
        sent_to_tag: "storel2_bts_forward_bo",
        Text: remark,
        sent_by: { 
          id: auth?.id, 
          name: getSingleuser?.[auth?.id]?.name, 
          office: user_current_office, 
          designation: auth?.designation?.[user_current_office] 
        },
        sent_to: { 
          id: row.req_creator_id, 
          name: getSingleuser?.[row.req_creator_id]?.name, 
          office: row?.req_data[0]?.sent_by?.office, 
          designation: getSingleuser?.[row.req_creator_id]?.designation?.[row?.req_data[0]?.sent_by?.office] 
        },
        action: `${isPartialIssue ? 'Partial' : 'Full'} consumables issue approved and forwarded to BO`,
        curr_sent_to_string: row?.req_creator_details,
        time_stamp: new Date()
      };
      
      const body = { id: row?._id, senttoData: senttoData };
      
      await updateConsumableReq(body).unwrap();
      alert(`${isPartialIssue ? 'Partial' : 'Full'} consumables issue approved and forwarded to BO for final acceptance.`);
      handleBack();
    } catch (err) {
      setError(`Failed to forward request: ${err.message || 'Unknown error'}`);
      console.error("Error forwarding request:", err);
      alert("There was an error forwarding the request. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [
    remark, auth, getSingleuser, user_current_office, 
    isPartialIssue, row?._id, updateConsumableReq, handleBack
  ]);

  return (
    <>
      {error && (
        <ErrorMessage>
          {error}
          <ErrorCloseButton onClick={() => setError('')}>×</ErrorCloseButton>
        </ErrorMessage>
      )}
      
      {isLoading && (
        <LoadingOverlay>
          <LoadingText>
            <CircularProgress size={24} /> 
            Processing request...
          </LoadingText>
        </LoadingOverlay>
      )}

      {/* Remarks textarea at the top */}
      <RemarkContainer>
        <TextField
          fullWidth
          label="Remarks"
          variant="outlined"
          multiline
          rows={4}
          value={remark}
          onChange={(e) => setRemark(e.target.value)}
          placeholder="Add your remarks here..."
          disabled={isLoading}
        />
      </RemarkContainer>
      
      {/* Display the issued consumables */}
      {latestConsumables.length > 0 && (
        <SelectedAssetsContainer>
          <SelectedAssetsTitleContainer>
            <SelectedAssetsTitle>Consumables Issued by Store Level 1</SelectedAssetsTitle>
          </SelectedAssetsTitleContainer>

          {/* Summary section */}
          <ConsumablesSummary items={latestConsumables} generateSummary={generateSummary} />
          
          <StyledTableContainer component={Paper}>
            <ConsumablesTable items={latestConsumables} getRequestedQuantity={getRequestedQuantity} />
          </StyledTableContainer>
          
          {/* Notification message */}
          <NotificationMessage isPartial={isPartialIssue}>
            {isPartialIssue 
              ? 'Partial items selected for issue. Some requested items or quantities will not be issued.' 
              : 'All requested items selected for issue with full quantities.'}
          </NotificationMessage>
        </SelectedAssetsContainer>
      )}
      
      {/* Action buttons */}
      <div style={{ marginTop: '20px', display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
        <ForwardButton 
          onClick={handleForwardToBO} 
          disabled={isLoading || latestConsumables.length === 0}
        >
          Approve & Forward to BO for Acceptance
        </ForwardButton>
        
        <ReturnButton 
          onClick={handleReturnToStoreL1} 
          disabled={isLoading}
        >
          Return to Store Level 1
        </ReturnButton>
      </div>
    </>
  );
};

// Apply memo to component before exporting
const MemoizedStoreL2InboxFormBTS = React.memo(StoreL2InboxFormBTS);
export default MemoizedStoreL2InboxFormBTS;